import { SectionStatus, SectionStatusType } from '../redux/types';
import { html, TemplateResult } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { ModuleId, Policy, SectionId, TreePath } from '@nirvana/shared/dtos/types/policies/common';
import { translateFn } from '../../../../utils/translator.helper';
import { FormGroupValue } from '../../../../ui/gzForm/formGroup/types/form-group-value';
import { Feature } from '@nirvana/shared/constants';

const STATUS_CONDITIONS = {
  [ModuleId.GENERAL]: {
    [SectionId.SECURITY_TELEMETRY]: (policy: Policy | null) => [
      policy?.uiSettings[ModuleId.GENERAL]?.securityTelemetry.enabled, // Security Telemetry
    ],
    [SectionId.UPDATE]: (policy: Policy | null) => [
      policy?.uiSettings[ModuleId.GENERAL]?.update.settings.productUpdateScheduler.enabled, // Product update
      policy?.uiSettings[ModuleId.GENERAL]?.update.settings.signatureUpdateScheduler.enabled, // Security content update
    ],
    [SectionId.GENERAL_NOTIFICATIONS]: (policy: Policy | null) => [
      !policy?.uiSettings[ModuleId.GENERAL]?.display.silentMode.enable, // Notifications
    ],
  },
  [ModuleId.ANTIMALWARE]: {
    [SectionId.ANTIMALWARE_ON_ACCESS]: (policy: Policy | null) => [
      policy?.uiSettings[ModuleId.ANTIMALWARE]?.onAccess.onAccessScanning.enable, // On-access Scanning
    ],
    [SectionId.ANTIMALWARE_ON_EXECUTE]: (policy: Policy | null) => [
      ...(Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_EDR_VISIBLE])
        ? [policy?.uiSettings[ModuleId.ANTIMALWARE]?.onExecute.theta.enabled]
        : []), // Cloud based threat detection
      policy?.uiSettings[ModuleId.ANTIMALWARE]?.onExecute.virusControl.enable, // Advanced Threat Control
      ...(Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_FILELESS_ATTACK_VISIBLE])
        ? [policy?.uiSettings[ModuleId.ANTIMALWARE]?.onExecute.commandLineScanning.filelessAttackProtection]
        : []), // Fileless Attack Protection
      ...(!Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE]) &&
      Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_RANSOMWARE_REMEDIATION_VISIBLE])
        ? [policy?.uiSettings[ModuleId.ANTIMALWARE]?.onExecute.ransomwareRemediation.enable]
        : []), // Ransomware Mitigation
    ],
    [SectionId.ANTIMALWARE_ON_DEMAND]: (policy: Policy | null) => [
      policy?.uiSettings[ModuleId.ANTIMALWARE]?.onDemand.scanTask.length, // Scan tasks grid items
    ],
    [SectionId.ANTIMALWARE_ANTI_TAMPERING]: (policy: Policy | null) => [
      policy?.uiSettings[ModuleId.ANTIMALWARE]?.antiTampering.enabled, // Anti-tampering
    ],
    [SectionId.ANTIMALWARE_DYNAMIC_THREAT_DEFENSE]: (policy: Policy | null) => [
      policy?.uiSettings[ModuleId.ANTIMALWARE]?.dynamicThreatDefense.enable, // Hyper Detect
    ],
    [SectionId.ANTIMALWARE_ANTI_EXPLOIT]: (policy: Policy | null) => [
      policy?.uiSettings[ModuleId.ANTIMALWARE]?.antiExploit.enable, // Advanced Anti-exploit
    ],
  },
  [ModuleId.SANDBOX]: {
    [SectionId.ENDPOINT_SENSOR]: (policy: Policy | null) => [
      policy?.uiSettings.networkSandboxing?.enabled, // Automatic sample submission from managed endpoints
    ],
    [SectionId.NETWORK_SENSOR]: (policy: Policy | null) => [
      policy?.uiSettings.ghostr?.enabled, // Automatic samples submission from network sensor
    ],
    [SectionId.ICAP_SENSOR]: (policy: Policy | null) => [
      policy?.uiSettings.icapSensor?.enabled, // Automatic samples submission from ICAP sensor
    ],
  },
  [ModuleId.FIREWALL]: {
    [SectionId.SETTINGS]: (policy: Policy | null) => [
      policy?.uiSettings[ModuleId.FIREWALL]?.settings.enable, // Firewall
      policy?.uiSettings[ModuleId.FIREWALL]?.settings.instructionsDetectionSystem.enable, // Intrusion Detection System
    ],
  },
  [ModuleId.CONTENT_CONTROL]: {
    [SectionId.CONTENT_CONTROL_TRAFFIC]: (policy: Policy | null) => [
      policy?.uiSettings[ModuleId.CONTENT_CONTROL]?.traffic.enable, // Network Protection
    ],
    [SectionId.CONTENT_CONTROL_WEB]: (policy: Policy | null) => [
      policy?.uiSettings[ModuleId.CONTENT_CONTROL]?.webAccess.enable &&
        policy?.uiSettings[ModuleId.CONTENT_CONTROL]?.webCategoriesFilter.scheduleId, // Web Access Control & Schedule selected
      policy?.uiSettings[ModuleId.CONTENT_CONTROL]?.application.enable &&
        policy?.uiSettings[ModuleId.CONTENT_CONTROL]?.application.rules.length, // Application Blacklist & at least one rule
      policy?.uiSettings[ModuleId.CONTENT_CONTROL]?.dataProtection.enable &&
        policy?.uiSettings[ModuleId.CONTENT_CONTROL]?.dataProtection.rules.length, // Data Protection & at least one rule
    ],
    [SectionId.CONTENT_CONTROL_DATA_PROTECTION]: (policy: Policy | null) => [
      policy?.uiSettings[ModuleId.CONTENT_CONTROL]?.antiphishing.enable, // Antiphishing
      policy?.uiSettings[ModuleId.CONTENT_CONTROL]?.traffic.trafficScan.webTraffic, // Web Traffic Scan
      ...(!Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE])
        ? [policy?.uiSettings[ModuleId.CONTENT_CONTROL]?.traffic.trafficScan.emailTraffic]
        : []), // Email Traffic Scan
    ],
    [SectionId.CONTENT_CONTROL_NETWORK_MONITOR]: (policy: Policy | null) => [
      policy?.uiSettings[ModuleId.CONTENT_CONTROL]?.networkMonitor.enable, // Network Attack Defense
    ],
  },
  [ModuleId.PATCH_MANAGEMENT]: {
    [SectionId.NONE]: (policy: Policy | null) => [
      policy?.uiSettings[ModuleId.PATCH_MANAGEMENT]?.maintenanceWindows.length, // Maintenance windows
    ],
  },
  [ModuleId.APPLICATION_CONTROL]: {
    [SectionId.NONE]: (policy: Policy | null) => [policy?.uiSettings[ModuleId.APPLICATION_CONTROL]?.general.enable],
  },
  [ModuleId.DEVICE_CONTROL]: {
    [SectionId.RULES]: (policy: Policy | null) => [
      policy?.uiSettings[ModuleId.DEVICE_CONTROL]?.dataLossPrevention.enable, // Device Control
    ],
  },
  [ModuleId.INTEGRITY_MONITOR]: {
    [SectionId.INTEGRITY_MONITOR_REAL_TIME]: (policy: Policy | null) => [
      policy?.uiSettings[ModuleId.INTEGRITY_MONITOR]?.realTime.enabled, // Integrity Monitoring
    ],
  },
  [ModuleId.EXCHANGE_PROTECTION]: {
    [SectionId.GENERAL]: (policy: Policy | null) => [
      policy?.uiSettings[ModuleId.EXCHANGE_PROTECTION]?.blackList.enable &&
        policy?.uiSettings[ModuleId.EXCHANGE_PROTECTION]?.blackList.list.length, // Connection blacklist & at least one item
      policy?.uiSettings[ModuleId.EXCHANGE_PROTECTION]?.antispoofing.enable &&
        policy?.uiSettings[ModuleId.EXCHANGE_PROTECTION]?.antispoofing.domainIpList.length, // Domain IP Check (Antispoofing) & at least one item
    ],
    [SectionId.EXCHANGE_ANTIMALWARE]: (policy: Policy | null) => [
      policy?.uiSettings[ModuleId.EXCHANGE_PROTECTION]?.antimalware.enable, // Antimalware filtering
    ],
    [SectionId.EXCHANGE_ANTISPAM]: (policy: Policy | null) => [
      policy?.uiSettings[ModuleId.EXCHANGE_PROTECTION]?.antispam.enable, // Antispam
    ],
    [SectionId.EXCHANGE_CONTENT_CONTROL]: (policy: Policy | null) => [
      policy?.uiSettings[ModuleId.EXCHANGE_PROTECTION]?.contentControl.contentFiltering.enable &&
        policy?.uiSettings[ModuleId.EXCHANGE_PROTECTION]?.contentControl.contentFiltering.rules.length, // Content Filtering & at least one item
      policy?.uiSettings[ModuleId.EXCHANGE_PROTECTION]?.contentControl.attachmentFiltering.enable &&
        policy?.uiSettings[ModuleId.EXCHANGE_PROTECTION]?.contentControl.attachmentFiltering.rules.length, // Attachment Filtering & at least one item
    ],
  },
  [ModuleId.ENCRYPTION]: {
    [SectionId.GENERAL]: (policy: Policy | null) => [policy?.uiSettings[ModuleId.ENCRYPTION]?.enabled], // Encryption
  },
  [ModuleId.EDR_SENSOR]: {
    [SectionId.GENERAL]: (policy: Policy | null) => [policy?.uiSettings[ModuleId.EDR_SENSOR]?.general.enabled], // Incidents Sensor
  },
  [ModuleId.STORAGE_PROTECTION]: {
    [SectionId.STORAGE_PROTECTION_ICAP]: (policy: Policy | null) => [
      policy?.uiSettings[ModuleId.STORAGE_PROTECTION]?.icap.enabled, // Storage Protection
    ],
  },
  [ModuleId.INDICATORS_OF_RISK]: {
    [SectionId.NONE]: (policy: Policy | null) => [policy?.uiSettings[ModuleId.INDICATORS_OF_RISK]?.enabled], // Risk Management
  },
  [ModuleId.LIVE_SEARCH]: {
    [SectionId.NONE]: (policy: Policy | null) => [policy?.uiSettings[ModuleId.LIVE_SEARCH]?.enabled], // Live Search
  },
  [ModuleId.NSX]: {
    [SectionId.NONE]: (policy: Policy | null) => [policy?.uiSettings[ModuleId.NSX]?.enabled], // NXS
  },
  [ModuleId.BLOCKLIST_RULES]: {
    [SectionId.NONE]: (policy: Policy | null) =>
      [policy?.uiSettings[ModuleId.BLOCKLIST_RULES]?.enabled],
  }
};

export function getSectionStatusFromPolicy(path: TreePath, policy: Policy | FormGroupValue | null): SectionStatus {
  // get conditions
  const conditions = STATUS_CONDITIONS[path.moduleId][path.sectionId](policy);

  // if there is only one condition, return the status directly
  if (conditions.length === 1) {
    return conditions[0] ? { type: SectionStatusType.ON } : { type: SectionStatusType.OFF };
  }

  // count the number of conditions that are on and off
  const onCount = conditions.filter(condition => !!condition).length;
  const offCount = conditions.filter(condition => !condition).length;

  if (onCount === conditions.length) {
    return { type: SectionStatusType.ON };
  }

  if (offCount === conditions.length) {
    return { type: SectionStatusType.OFF };
  }

  // if there are both on and off conditions, return partial status
  return { type: SectionStatusType.PARTIAL, progress: { count: onCount, total: conditions.length } };
}

const onStatusRenderer = (): TemplateResult => {
  return html`
    <gz-tooltip tooltipText="${translateFn('This section is enabled.')}">
      <gz-icon color="var(--blue-layout-405-primary-hc-on-light-background)" size="24">
        <svg slot="custom-svg" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
          <g>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M7.2 19H16.8C20.736 19 24 15.8267 24 12C24 8.17333 20.736 5 16.8 5H7.2C3.264 5 0 8.17333 0 12C0 15.8267 3.264 19 7.2 19ZM0.96 12C0.96 8.64 3.744 5.93333 7.2 5.93333H16.8C20.256 5.93333 23.04 8.64 23.04 12C23.04 15.36 20.256 18.0667 16.8 18.0667H7.2C3.744 18.0667 0.96 15.36 0.96 12ZM8.69458 15.5726C7.05599 15.5726 6 14.4165 6 12.2863C6 10.1561 7.05599 9 8.69458 9C10.3332 9 11.3892 10.1561 11.3892 12.2863C11.3892 14.4165 10.3332 15.5726 8.69458 15.5726ZM8.69458 14.8899C9.79609 14.8899 10.5699 14.0706 10.5699 12.8143V11.7583C10.5699 10.502 9.79609 9.68275 8.69458 9.68275C7.59308 9.68275 6.8193 10.502 6.8193 11.7583V12.8143C6.8193 14.0706 7.59308 14.8899 8.69458 14.8899ZM16.5651 15.5726L14.164 11.5612L13.373 10.1017H13.3447V15.5726H12.5726V9H13.486L15.8871 13.0114L16.6781 14.4709H16.7064V9H17.4785V15.5726H16.5651Z"/>
          </g>
        </svg>
      </gz-icon>
    </gz-tooltip>
  `;
};

const offStatusRenderer = (): TemplateResult => {
  return html`
    <gz-tooltip tooltipText="${translateFn('This section is disabled.')}">
      <gz-icon color="var(--chart-300-icon-buttons)" size="24">
        <svg slot="custom-svg" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M7.2 19H16.8C20.736 19 24 15.8267 24 12C24 8.17333 20.736 5 16.8 5H7.2C3.264 5 0 8.17333 0 12C0 15.8267 3.264 19 7.2 19ZM0.96 12C0.96 8.64 3.744 5.93333 7.2 5.93333H16.8C20.256 5.93333 23.04 8.64 23.04 12C23.04 15.36 20.256 18.0667 16.8 18.0667H7.2C3.744 18.0667 0.96 15.36 0.96 12ZM7.45983 15C5.96399 15 5 13.9446 5 12C5 10.0554 5.96399 9 7.45983 9C8.95568 9 9.91967 10.0554 9.91967 12C9.91967 13.9446 8.95568 15 7.45983 15ZM7.45983 14.3767C8.46537 14.3767 9.17175 13.6288 9.17175 12.482V11.518C9.17175 10.3712 8.46537 9.62327 7.45983 9.62327C6.45429 9.62327 5.74792 10.3712 5.74792 11.518V12.482C5.74792 13.6288 6.45429 14.3767 7.45983 14.3767ZM11.8726 14.9003H11.1746V9.09972H14.6316V9.71468H11.8726V11.6593H14.3906V12.2742H11.8726V14.9003ZM15.8166 14.9003H16.5147V12.2742H19.0327V11.6593H16.5147V9.71468H19.2737V9.09972H15.8166V14.9003Z"/>
        </svg>
      </gz-icon>
    </gz-tooltip>
  `;
};

const warningStatusRenderer = (): TemplateResult => {
  return html`
    <gz-tooltip tooltipText="${translateFn('This section has issues.')}">
      <gz-icon color="var(--orange-layout-050-warning)" size="24">
        <svg slot="custom-svg" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
          <g>
            <g>
              <path
                d="M16.8 19H7.2C3.264 19 0 15.8267 0 12C0 8.17333 3.264 5 7.2 5H16.8C20.736 5 24 8.17333 24 12C24 15.8267 20.736 19 16.8 19ZM7.2 5.93333C3.744 5.93333 0.96 8.64 0.96 12C0.96 15.36 3.744 18.0667 7.2 18.0667H16.8C20.256 18.0667 23.04 15.36 23.04 12C23.04 8.64 20.256 5.93333 16.8 5.93333H7.2Z"
              ></path>
              <path
                d="M12.6721 16.2001C12.7681 16.2001 12.8641 16.1068 12.8641 16.0134V14.6134C12.8641 14.5201 12.7681 14.4268 12.6721 14.4268H11.3281C11.2321 14.4268 11.1361 14.5201 11.1361 14.6134V16.0134C11.1361 16.1068 11.2321 16.2001 11.3281 16.2001H12.6721Z"
              ></path>
              <path
                d="M12.6721 7.8H11.3281C11.2321 7.8 11.1361 7.89333 11.1361 7.98667V13.3067C11.1361 13.4 11.2321 13.4933 11.3281 13.4933H12.6721C12.7681 13.4933 12.8641 13.4 12.8641 13.3067V7.98667C12.8641 7.89333 12.7681 7.8 12.6721 7.8Z"
              ></path>
            </g>
          </g>
        </svg>
      </gz-icon>
    </gz-tooltip>
  `;
};

const progressBarStatusRenderer = (count: number, total: number, tooltipText: string): TemplateResult => {
  return html`
    <gz-tooltip tooltipText="${tooltipText}">
      <span style="
        display: inline-grid;
        text-align: center;
        width: 23px;
        height: 14px;
        font-size: 9px;
        margin-bottom: 8px">
        <span style="display: inline-flex; font-family: var(--font-family-secondary)">
          <span style="color: var(--blue-layout-405-primary-hc-on-light-background); margin-left: 3px;">${count}</span>
          <span style="color: var(--grey-layout-200-disabled);">/${total}</span>
        </span>
        <span style="height: 2px; display: inline-flex; background-color: var(--grey-layout-200-disabled); border-radius: 13px;">
          <span style="background-color: var(--blue-layout-405-primary-hc-on-light-background); width: 50%; height: 2px; border-radius: 10px;"></span>
        </span
      </span>
    </gz-tooltip>
  `;
};

const undefinedStatusRenderer = (): TemplateResult => {
  return html`
    <gz-tooltip tooltipText="${translateFn('Only some settings in this section are enabled.')}">
      <gz-icon size="24" color="var(--blue-layout-405-primary-hc-on-light-background)">
        <svg slot="custom-svg" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
          <g>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M7.2 19H16.8C20.736 19 24 15.8267 24 12C24 8.17333 20.736 5 16.8 5H7.2C3.264 5 0 8.17333 0 12C0 15.8267 3.264 19 7.2 19ZM0.96 12C0.96 8.64 3.744 5.93333 7.2 5.93333H16.8C20.256 5.93333 23.04 8.64 23.04 12C23.04 15.36 20.256 18.0667 16.8 18.0667H7.2C3.744 18.0667 0.96 15.36 0.96 12ZM9 11C8.44771 11 8 11.4477 8 12C8 12.5523 8.44771 13 9 13H15C15.5523 13 16 12.5523 16 12C16 11.4477 15.5523 11 15 11H9Z" />
          </g>
        </svg>
      </gz-icon>
    </gz-tooltip>
  `;
};

export function getStatusRendererForSection(status: SectionStatus): TemplateResult {
  switch (status.type) {
    case SectionStatusType.NONE:
      return html``;
    case SectionStatusType.ON:
      return onStatusRenderer();
    case SectionStatusType.OFF:
      return offStatusRenderer();
    case SectionStatusType.WARNING:
      return warningStatusRenderer();
    case SectionStatusType.PARTIAL:
      return progressBarStatusRenderer(
        status.progress?.count ?? 0,
        status.progress?.total ?? 0,
        translateFn('Only some settings in this section are enabled.')
      );
    default:
      return html``;
  }
}

export function getStatusRendererForModule(sectionStatuses: SectionStatus[]): TemplateResult {
  const result = sectionStatuses.reduce(
    (acc, status: SectionStatus) => {
      if (status.type === SectionStatusType.WARNING) {
        acc.warningNumber++;
      }
      if (status.type === SectionStatusType.OFF) {
        acc.offNumber++;
      }
      if (status.type === SectionStatusType.ON) {
        acc.onNumber++;
      }
      if (status.type === SectionStatusType.PARTIAL) {
        acc.partialNumber++;
      }

      if (status.type === SectionStatusType.NONE) {
        acc.noneNumber++;
      }

      return acc;
    },
    { onNumber: 0, offNumber: 0, warningNumber: 0, partialNumber: 0, noneNumber: 0 }
  );

  if (result.warningNumber) {
    return getStatusRendererForSection({ type: SectionStatusType.WARNING });
  }

  if (result.noneNumber === sectionStatuses.length) {
    return getStatusRendererForSection({ type: SectionStatusType.NONE });
  }

  const totalCount = result.onNumber + result.offNumber + result.partialNumber;
  if (result.onNumber === totalCount) {
    return getStatusRendererForSection({ type: SectionStatusType.ON });
  }

  if (result.offNumber === totalCount) {
    return getStatusRendererForSection({ type: SectionStatusType.OFF });
  }

  if (result.onNumber > 0) {
    return progressBarStatusRenderer(result.onNumber, totalCount, translateFn('Only some sections are enabled.'));
  }

  return undefinedStatusRenderer();
}
